import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { Select } from '@/components/ui/Select';
import { useAsyncData } from '@/hooks';
import { ApiError } from '@/api/client';
import { institutionTemplatesApi } from '@/api/institution-templates';
import {
  HIERARCHY_UNIT_TYPES,
  TEMPLATE_TERMINOLOGY_KEYS,
  isDeliveryUnitType,
  type HierarchyRung,
  type InstitutionTemplateDefinition,
} from '@/types/institution-templates';
import { HierarchyDiagram } from './HierarchyDiagram';
import { humanizeUnitType } from './hierarchy-layout';
import styles from './platform-pages.module.css';

/**
 * One hierarchy template in full: the ladder drawn, the vocabulary, the academic
 * model, the offices it seeds — and an experiment mode that previews a
 * customisation WITHOUT PERSISTING ANYTHING.
 *
 * THE EXPERIMENT IS THE SERVER'S, NOT OURS. Edited labels and dropped rungs go to
 * POST /institution-templates/resolve/customised, and what renders as the preview
 * is the server's merged, validated answer — including the positions that silently
 * fall away when the rung they anchor to is removed. Computing that merge locally
 * would drift from the one implementation that matters the day a rule changes.
 *
 * CAPABILITY-CLASS BOUNDS ARE DISABLED UP FRONT. A SUPERVISORY or AGGREGATE
 * template can never gain a delivery unit type (CLASS_GROUP, COURSE_OFFERING,
 * MODULE, WORKSHOP_GROUP, INTAKE_COHORT, STREAM) — identity answers 403
 * PRIVILEGE_ESCALATION_DENIED. The picker greys those options out with the reason
 * attached, so the rule is learned from the control instead of from the rejection;
 * the server still enforces it, because a disabled option is presentation, not
 * security. There is deliberately NO capability-class editor at all: the class is
 * immutable server-side, and a control that can only ever fail is not a control.
 */

interface ExperimentRung {
  /** Stable identity for React keys across removals — index would reshuffle. */
  id: string;
  unitType: string;
  label: string;
  required: boolean;
  /** Only optional rungs can be excluded; required ones always send true. */
  included: boolean;
  /** True for rungs the operator appended, which can be removed entirely. */
  added: boolean;
}

function toExperimentRungs(template: InstitutionTemplateDefinition): ExperimentRung[] {
  return template.hierarchy.map((rung, index) => ({
    id: `base-${index}-${rung.unitType}`,
    unitType: rung.unitType,
    label: rung.label,
    required: rung.required,
    included: true,
    added: false,
  }));
}

/** The hierarchy the preview request sends: included rungs, in order, depth recomputed. */
export function experimentHierarchy(rungs: ExperimentRung[]): HierarchyRung[] {
  return rungs
    .filter((rung) => rung.included)
    .map((rung, index) => ({
      unitType: rung.unitType,
      depth: index + 1,
      label: rung.label.trim() || humanizeUnitType(rung.unitType),
      required: rung.required,
    }));
}

type PreviewState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; template: InstitutionTemplateDefinition }
  | { status: 'error'; message: string; httpStatus: number };

export function InstitutionTemplateDetailPage() {
  const { t } = useTranslation();
  const { code = '' } = useParams();

  const { state, reload } = useAsyncData<InstitutionTemplateDefinition>(
    `institution-template-${code}`,
    () => institutionTemplatesApi.byCode(code),
    !!code,
  );

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('templates.breadcrumb')}>
        <Link to="/admin/institution-templates">{t('templates.title')}</Link>
        <Icon name="chevronRight" size={14} aria-hidden="true" />
        <span>{state.status === 'ready' ? state.data.displayName : code}</span>
      </nav>

      <AsyncPanel state={state} onRetry={reload}>
        {(template) => <TemplateDetail template={template} />}
      </AsyncPanel>
    </div>
  );
}

function TemplateDetail({ template }: { template: InstitutionTemplateDefinition }) {
  const { t } = useTranslation();

  const unitName = (unitType: string) =>
    t(`templates.unitTypes.${unitType}`, { defaultValue: humanizeUnitType(unitType) });

  /** The rung a position anchors to, named by its ladder label when it has one. */
  const anchorLabel = (unitType: string) => {
    const rung = template.hierarchy.find((entry) => entry.unitType === unitType);
    return rung ? rung.label : unitName(unitType);
  };

  return (
    <>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{template.displayName}</h1>
          <p className={styles.pageLead}>{template.description}</p>
          <div className={styles.rowMetaLine}>
            <code className={styles.codeTag}>{template.code}</code>
            <span className={styles.infoTag}>
              {t(`templates.subsystems.${template.educationSubsystem}`, {
                defaultValue: template.educationSubsystem,
              })}
            </span>
            <span
              className={template.capabilityClass === 'OPERATIONAL' ? styles.okTag : styles.warnTag}
              title={t(`templates.capabilityClassHints.${template.capabilityClass}`)}
            >
              {t(`templates.capabilityClasses.${template.capabilityClass}`)}
            </span>
            {template.needsReview && (
              <span className={styles.warningTag} title={t('templates.needsReviewHint')}>
                {t('templates.needsReview')}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className={styles.gridTwo}>
        <Panel title={t('templates.diagram.title')} icon="institution">
          <HierarchyDiagram
            hierarchy={template.hierarchy}
            auxiliaryUnitTypes={template.auxiliaryUnitTypes}
            ariaLabel={t('templates.diagram.aria', {
              name: template.displayName,
              total: template.hierarchy.length,
            })}
          />
          {template.sections.length > 0 && <SectionsList sections={template.sections} />}
          {template.levelSource && (
            <p className={styles.footnote}>
              {t('templates.levelSourceNote', {
                model: template.levelSource.institutionModel,
                source: template.levelSource.source,
              })}
            </p>
          )}
        </Panel>

        <div>
          <Panel title={t('templates.terminology.title')} icon="globe">
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">{t('templates.terminology.concept')}</th>
                    <th scope="col">{t('templates.terminology.word')}</th>
                  </tr>
                </thead>
                <tbody>
                  {TEMPLATE_TERMINOLOGY_KEYS.map((key) => (
                    <tr key={key}>
                      <th scope="row" className={styles.tableRowHeader}>
                        {t(`templates.terminology.keys.${key}`)}
                      </th>
                      <td>
                        {template.terminology[key] ?? (
                          <span className={styles.mutedCell}>{t('templates.terminology.unset')}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>

          <AcademicModelPanel template={template} />
        </div>
      </div>

      <Panel title={t('templates.positions.title')} icon="role">
        {template.defaultPositions.length === 0 ? (
          <p className={styles.sectionEmpty}>{t('templates.positions.empty')}</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">{t('templates.positions.office')}</th>
                  <th scope="col">{t('templates.positions.anchor')}</th>
                  <th scope="col">{t('templates.positions.selector')}</th>
                  <th scope="col">{t('templates.positions.capabilities')}</th>
                </tr>
              </thead>
              <tbody>
                {template.defaultPositions.map((position) => (
                  <tr key={`${position.positionCode}-${position.scopeUnitType}-${position.scopeSelector ?? ''}`}>
                    <td>
                      <code className={styles.codeTag}>{position.positionCode}</code>
                    </td>
                    <td>
                      {anchorLabel(position.scopeUnitType)}{' '}
                      <span className={styles.mutedCell}>({unitName(position.scopeUnitType)})</span>
                    </td>
                    <td>
                      {position.scopeSelector ? (
                        <code className={styles.codeTag}>{position.scopeSelector}</code>
                      ) : (
                        <span className={styles.mutedCell}>{t('templates.positions.everyUnit')}</span>
                      )}
                    </td>
                    <td>
                      <ul className={styles.chipRow}>
                        {position.capabilities.map((capability) => (
                          <li key={capability} className={styles.chip}>
                            {humanizeUnitType(capability)}
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {template.defaultRoleBundleCodes.length > 0 && (
          <>
            <h3 className={styles.subheading}>{t('templates.bundles.title')}</h3>
            <ul className={styles.chipRow}>
              {template.defaultRoleBundleCodes.map((bundle) => (
                <li key={bundle} className={styles.chip}>
                  <code>{bundle}</code>
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>

      <ExperimentPanel template={template} />
    </>
  );
}

/** Sections and cycles, beneath the ladder — the school templates' second axis. */
function SectionsList({ sections }: { sections: InstitutionTemplateDefinition['sections'] }) {
  const { t } = useTranslation();
  return (
    <div>
      <h3 className={styles.subheading}>{t('templates.sections.title')}</h3>
      <ul className={styles.treeList}>
        {sections.map((section) => (
          <li key={section.code} className={styles.treeBranch}>
            <span className={styles.rowTitle}>{section.label}</span>{' '}
            <code className={styles.codeTag}>{section.code}</code>
            <ul className={styles.treeBranches}>
              {section.cycles.map((cycle) => (
                <li key={cycle.code} className={styles.treeCampus}>
                  {cycle.label}
                  {/* levelRangeLabel is display text ONLY — never parsed, never a picker. */}
                  {cycle.levelRangeLabel && (
                    <span className={styles.mutedCell}> · {cycle.levelRangeLabel}</span>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AcademicModelPanel({ template }: { template: InstitutionTemplateDefinition }) {
  const { t } = useTranslation();
  const model = template.academicModel;
  const fact = (label: string, value: string) => (
    <div className={styles.statusPair}>
      <span className={styles.statusPairLabel}>{label}</span>
      <span>{value}</span>
    </div>
  );
  return (
    <Panel title={t('templates.academic.title')} icon="academic">
      <div className={styles.detailGrid}>
        {fact(t('templates.academic.kind'), humanizeUnitType(model.kind))}
        {fact(
          t('templates.academic.calendar'),
          t('templates.academic.calendarValue', {
            periods: model.calendar.periodsPerYear,
            type: humanizeUnitType(model.calendar.periodType),
            month: model.calendar.typicalStartMonth,
          }),
        )}
        {fact(
          t('templates.academic.grading'),
          model.grading.passMark === null
            ? humanizeUnitType(model.grading.scheme)
            : t('templates.academic.gradingValue', {
                scheme: humanizeUnitType(model.grading.scheme),
                passMark: model.grading.passMark,
              }),
        )}
        {fact(
          t('templates.academic.progression'),
          t(
            model.progression.repetitionAllowed
              ? 'templates.academic.progressionRepeats'
              : 'templates.academic.progressionNoRepeats',
            { mode: humanizeUnitType(model.progression.mode) },
          ),
        )}
      </div>
      {model.certifications.length > 0 && (
        <>
          <h3 className={styles.subheading}>{t('templates.academic.certifications')}</h3>
          <ul className={styles.chipRow}>
            {model.certifications.map((certification) => (
              <li key={certification} className={styles.chip}>
                {certification}
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}

function ExperimentPanel({ template }: { template: InstitutionTemplateDefinition }) {
  const { t } = useTranslation();

  const [rungs, setRungs] = useState<ExperimentRung[]>(() => toExperimentRungs(template));
  const [addUnitType, setAddUnitType] = useState('');
  const [preview, setPreview] = useState<PreviewState>({ status: 'idle' });
  const [nextId, setNextId] = useState(0);

  const operational = template.capabilityClass === 'OPERATIONAL';

  const unitName = (unitType: string) =>
    t(`templates.unitTypes.${unitType}`, { defaultValue: humanizeUnitType(unitType) });

  const addOptions = useMemo(
    () =>
      HIERARCHY_UNIT_TYPES.map((unitType) => {
        const blocked = !operational && isDeliveryUnitType(unitType);
        return {
          value: unitType,
          label: unitName(unitType),
          // The reason travels WITH the disabled option: the operator reads why this
          // row is off instead of discovering a 403.
          description: blocked
            ? t('templates.experiment.deliveryBlocked', {
                class: t(`templates.capabilityClasses.${template.capabilityClass}`),
              })
            : undefined,
          disabled: blocked,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operational, template.capabilityClass, t],
  );

  const update = (id: string, patch: Partial<ExperimentRung>) =>
    setRungs((current) => current.map((rung) => (rung.id === id ? { ...rung, ...patch } : rung)));

  const addRung = () => {
    if (!addUnitType) return;
    // Belt and braces: the option is disabled in the picker, but a stale closure or
    // future refactor must not let a delivery rung slip into a supervisory body.
    if (!operational && isDeliveryUnitType(addUnitType)) return;
    setRungs((current) => [
      ...current,
      {
        id: `added-${nextId}`,
        unitType: addUnitType,
        label: humanizeUnitType(addUnitType),
        required: true,
        included: true,
        added: true,
      },
    ]);
    setNextId((value) => value + 1);
    setAddUnitType('');
  };

  const runPreview = async () => {
    setPreview({ status: 'loading' });
    try {
      const result = await institutionTemplatesApi.previewCustomised({
        institutionType: template.institutionType,
        educationSubsystem: template.educationSubsystem,
        hierarchy: experimentHierarchy(rungs),
      });
      setPreview({ status: 'ready', template: result });
    } catch (cause) {
      // The 400/403 sentences name the exact rule broken ("These rungs are
      // required for ... and cannot be removed") — shown as written.
      setPreview({
        status: 'error',
        message: cause instanceof Error ? cause.message : t('common.error'),
        httpStatus: cause instanceof ApiError ? cause.status : 0,
      });
    }
  };

  const reset = () => {
    setRungs(toExperimentRungs(template));
    setPreview({ status: 'idle' });
  };

  return (
    <Panel title={t('templates.experiment.title')} icon="settings">
      {/* Not dismissible, same rule as the console scope bar: the one fact that must
          survive a screenshot is that nothing here writes anything. */}
      <div className={styles.warningNotice} role="note">
        <Icon name="info" size={16} aria-hidden="true" />
        <div>
          <p className={styles.warningTitle}>{t('templates.experiment.previewOnlyTitle')}</p>
          <p className={styles.warningBody}>{t('templates.experiment.previewOnlyBody')}</p>
        </div>
      </div>

      <ul className={styles.rowList}>
        {rungs.map((rung) => (
          <li key={rung.id} className={styles.row}>
            <div className={styles.rowMain}>
              <div className={styles.rowMetaLine}>
                <span className={styles.codeTag}>{unitName(rung.unitType)}</span>
                {rung.required ? (
                  <span className={styles.mutedTag} title={t('templates.experiment.requiredHint')}>
                    {t('templates.experiment.required')}
                  </span>
                ) : (
                  <span className={styles.infoTag}>{t('templates.diagram.optional')}</span>
                )}
                {rung.added && <span className={styles.infoTag}>{t('templates.experiment.addedTag')}</span>}
              </div>
              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor={`rung-label-${rung.id}`}>
                  {t('templates.experiment.labelField')}
                </label>
                <input
                  id={`rung-label-${rung.id}`}
                  className={styles.input}
                  value={rung.label}
                  maxLength={80}
                  disabled={!rung.included}
                  onChange={(event) => update(rung.id, { label: event.target.value })}
                />
              </div>
            </div>
            <div className={styles.rowAside}>
              {rung.added ? (
                <button
                  type="button"
                  className={styles.linkBtnDanger}
                  onClick={() => setRungs((current) => current.filter((entry) => entry.id !== rung.id))}
                >
                  {t('common.remove')}
                </button>
              ) : (
                <label className={styles.checkboxField}>
                  <input
                    type="checkbox"
                    checked={rung.included}
                    // A required rung cannot be dropped — the server refuses with a 400,
                    // so the checkbox does not pretend otherwise.
                    disabled={rung.required}
                    title={rung.required ? t('templates.experiment.requiredHint') : undefined}
                    onChange={(event) => update(rung.id, { included: event.target.checked })}
                  />
                  {t('templates.experiment.include')}
                </label>
              )}
            </div>
          </li>
        ))}
      </ul>

      <div className={styles.dialogRow}>
        <div className={styles.field}>
          <span className={styles.fieldLabel} id="add-rung-label">
            {t('templates.experiment.addRung')}
          </span>
          <Select
            aria-labelledby="add-rung-label"
            value={addUnitType}
            onChange={setAddUnitType}
            options={addOptions}
            placeholder={t('templates.experiment.addRungPlaceholder')}
            searchable
          />
        </div>
        <button type="button" className={styles.secondaryBtn} onClick={addRung} disabled={!addUnitType}>
          {t('templates.experiment.addRungAction')}
        </button>
      </div>

      <div className={styles.filterActions}>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={runPreview}
          disabled={preview.status === 'loading'}
        >
          {preview.status === 'loading'
            ? t('templates.experiment.previewing')
            : t('templates.experiment.preview')}
        </button>
        <button type="button" className={styles.secondaryBtn} onClick={reset}>
          {t('templates.experiment.reset')}
        </button>
      </div>

      {preview.status === 'error' && (
        <div className={styles.warningNotice} role="alert">
          <Icon name="warning" size={16} aria-hidden="true" />
          <div>
            <p className={styles.warningTitle}>
              {preview.httpStatus === 403
                ? t('templates.experiment.refusedTitle')
                : t('templates.experiment.invalidTitle')}
            </p>
            {/* The server's own sentence — it names the rung or capability at fault. */}
            <p className={styles.warningBody}>{preview.message}</p>
          </div>
        </div>
      )}

      {preview.status === 'ready' && (
        <section aria-label={t('templates.experiment.resultTitle')}>
          <h3 className={styles.subheading}>{t('templates.experiment.resultTitle')}</h3>
          <HierarchyDiagram
            hierarchy={preview.template.hierarchy}
            auxiliaryUnitTypes={preview.template.auxiliaryUnitTypes}
            ariaLabel={t('templates.diagram.aria', {
              name: preview.template.displayName,
              total: preview.template.hierarchy.length,
            })}
          />
          <h3 className={styles.subheading}>{t('templates.experiment.resultPositions')}</h3>
          {preview.template.defaultPositions.length === 0 ? (
            <p className={styles.sectionEmpty}>{t('templates.positions.empty')}</p>
          ) : (
            <ul className={styles.chipRow}>
              {preview.template.defaultPositions.map((position) => (
                <li
                  key={`${position.positionCode}-${position.scopeUnitType}-${position.scopeSelector ?? ''}`}
                  className={styles.chip}
                >
                  <code>{position.positionCode}</code> → {unitName(position.scopeUnitType)}
                </li>
              ))}
            </ul>
          )}
          {/* Positions whose anchor rung was dropped vanish server-side; saying so
              beats leaving the operator to count the difference. */}
          {preview.template.defaultPositions.length < template.defaultPositions.length && (
            <p className={styles.footnote}>
              {t('templates.experiment.droppedPositions', {
                total: template.defaultPositions.length - preview.template.defaultPositions.length,
              })}
            </p>
          )}
        </section>
      )}
    </Panel>
  );
}
